import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, TextField, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Tooltip, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import StoreLocationIcon from '@mui/icons-material/StoreMallDirectory';
import LabelIcon from '@mui/icons-material/Label';

import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', dangerDark: '#dc2626',
};

function StoreLocationIndex() {
  const history = useHistory();
  const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();

  const hasCreateMasterPermission = permissions.includes('Create Master');
  const hasEditMasterPermission   = permissions.includes('Edit Master');
  const hasDeleteMasterPermission = permissions.includes('Delete Master');

  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Delete dialog
  const [deleteId, setDeleteId]   = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchTableData();
  }, [isAuthenticated, companyId, branchId, token]);

  const fetchTableData = async () => {
    setLoading(true);
    try {
      const res = await fetchData('StoreLocationApi', `/${companyId}`, token);
      if (res.responseCode === '1111') setData(res.response_data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDelete  = (id) => { setDeleteId(id); setDeleteOpen(true); };
  const closeDelete = () => { setDeleteId(null); setDeleteOpen(false); };

  const handleDelete = async () => {
    try {
      await deleteData('StoreLocationApi', `${deleteId}`, token);
      closeDelete();
      fetchTableData();
    } catch (err) {
      console.error('Error deleting StoreLocation:', err);
    }
  };

  const handleActivate = (id) => {
    activateData('StoreLocationApi', `${id}`, token)
      .then(() => fetchTableData())
      .catch(err => console.error('Error activating StoreLocation:', err));
  };

  const filteredData = data.filter(item =>
    Object.values(item).some(v =>
      String(v).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const btnDanger = {
    fontSize: '11px', fontWeight: 700, textTransform: 'none',
    bgcolor: C.danger, color: '#fff', borderRadius: '7px', px: 1.6, py: 0.5,
    '&:hover': { bgcolor: C.dangerDark },
  };
  const btnSecondary = {
    fontSize: '11px', fontWeight: 600, textTransform: 'none',
    color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
    borderRadius: '7px', px: 1.4, py: 0.5,
    '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
  };

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <StoreLocationIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            Store Location
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14, color: C.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 180,
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px', fontSize: '12px', height: '28px',
                bgcolor: C.card,
                '& fieldset': { borderColor: C.cardBorder },
                '&:hover fieldset': { borderColor: C.accentBorder },
                '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
              },
            }}
          />
          {hasCreateMasterPermission && (
            <Button
              component={Link} to="/StoreLocationCreate"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 13 }} />}
              sx={{
                fontSize: '11px', fontWeight: 700, textTransform: 'none',
                bgcolor: C.accent, color: '#fff', borderRadius: '7px',
                px: 1.4, py: 0.4, boxShadow: C.shadow,
                '&:hover': { bgcolor: C.accentDark },
              }}
            >
              Create
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Table card ── */}
      <Box sx={{
        bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow,
        border: `1px solid ${C.cardBorder}`, overflow: 'hidden',
      }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.8,
          px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`,
          bgcolor: `${C.accent}0d`,
        }}>
          <LabelIcon sx={{ fontSize: 15, color: C.accent }} />
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            Store Location
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Typography sx={{ fontSize: '10px', color: C.muted }}>
              {filteredData.length} record{filteredData.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: C.accent }} />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['#', 'Name', ''].map((h, i) => (
                    <TableCell key={i} sx={{
                      fontSize: '10px', fontWeight: 700, color: C.muted,
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      bgcolor: '#f8fafa', borderBottom: `1px solid ${C.cardBorder}`,
                      py: '6px', px: '10px',
                      ...(i === 2 ? { textAlign: 'right' } : {}),
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4, color: C.muted, fontSize: '12px' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((item, index) => (
                    <TableRow key={item.Id}
                      sx={{
                        '&:hover': { bgcolor: C.accentDim },
                        '&:last-child td': { borderBottom: 0 },
                        opacity: item.IsActive === false ? 0.5 : 1,
                      }}
                    >
                      <TableCell sx={{ fontSize: '12px', color: C.muted, py: '5px', px: '10px', width: 40 }}>{index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '12px', color: C.text, py: '5px', px: '10px', fontWeight: 500 }}>{item.Name}</TableCell>
                      <TableCell sx={{ py: '5px', px: '10px', textAlign: 'right', width: 90 }}>
                        {hasEditMasterPermission && (
                          <Tooltip title="Edit" arrow>
                            <IconButton size="small" component={Link} to={`/StoreLocationEdit/${item.Id}`}
                              sx={{ color: '#7c3aed', '&:hover': { bgcolor: 'rgba(124,58,237,0.08)' } }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasDeleteMasterPermission && (
                          item.IsActive ? (
                            <Tooltip title="Delete" arrow>
                              <IconButton size="small" onClick={() => openDelete(item.Id)}
                                sx={{ color: C.danger, '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activate" arrow>
                              <IconButton size="small" onClick={() => handleActivate(item.Id)}
                                sx={{ color: '#22c55e', '&:hover': { bgcolor: 'rgba(34,197,94,0.08)' } }}>
                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ fontSize: '13px', fontWeight: 800, color: C.text, borderBottom: `1px solid ${C.cardBorder}`, pb: 0.5 }}>
          Delete StoreLocation?
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <DialogContentText sx={{ fontSize: '12px', color: C.muted }}>
            Are you sure you want to delete this StoreLocation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
          <Button onClick={closeDelete} sx={btnSecondary}>Cancel</Button>
          <Button onClick={handleDelete} sx={btnDanger} startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default StoreLocationIndex;

